import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FastField } from 'formik';
import cx from 'classnames';
import { pascalize } from 'humps';
import ReactMarkdown from 'react-markdown';
import { fieldComponent } from '../../shared/data';
import { REQUIRE_QUESTION } from '../../shared/reducer';
import { useStateValue } from '../../shared/state';
import FormFieldError from '../FormFieldError';
import FormFieldHelp from '../FormFieldHelp';

import BravelyData from 'utils/data';
import { wordCount } from 'utils/strings';

import styles from './styles';
import { useTranslation } from 'react-i18next';

const needsAndDemographyEnabled = BravelyData('needsAndDemographyEnabled');
const useCallbackFieldRef = (focus, isValid, submitCount) =>
  useCallback(
    (node) => {
      if (node !== null && focus) {
        // Find the form field's first input
        const firstInput = node.querySelector('input, select, textarea');
        firstInput && firstInput.focus();
      }
    },
    [focus, isValid, submitCount]
  );

export default function FormField({
  question,
  context,
  required,
  hidden,
  error,
  touched,
  value,
  focus,
  submitCount,
  isValid,
  disabled,
}) {
  const fieldRef = useCallbackFieldRef(focus, isValid, submitCount);
  const [showHelp, setShowHelp] = useState(false);
  const [{ features }, dispatch] = useStateValue(); // eslint-disable-line no-empty-pattern

  const { t } = useTranslation();
  const isCheckbox = question.fieldType === 'checkbox';

  // If a question becomes unhidden
  // Prefer the question.config.required value
  useEffect(() => {
    if (!hidden && !required && question.config.required) {
      dispatch({
        type: REQUIRE_QUESTION,
        question: question,
      });
    }
  }, [hidden]);

  let checkBoxProps = {};

  if (isCheckbox) {
    checkBoxProps.checked = value;
  }

  const validateWordCount = (value, question) => {
    const requiredWordCount = question.config?.minimumWordCount;

    if (requiredWordCount && wordCount(value) < requiredWordCount) {
      return (
        question.config?.wordCountErrorMsg ||
        `A minimum of ${requiredWordCount} words is required.`
      );
    }
  };

  return (
    (!hidden && (
      <div
        ref={fieldRef}
        className={cx(
          styles.row,
          styles[`rowType${pascalize(question.fieldType)}`],
          {
            [styles.helpIsVisible]: showHelp,
            [styles.numberedFields]:
              features.numberedQuestions && !needsAndDemographyEnabled,
          }
        )}
      >
        {question.config?.header && (
          <div
            className={cx({
              [styles.header]: question.config.required,
              [styles.description]: !question.config.required,
            })}
          >
            <span> {question.config.header} </span>
          </div>
        )}
        {!isCheckbox && (
          <label className={styles.label} htmlFor={question.name}>
            <span className={styles.labelText}>{t(question.label)}</span>
            {question.config?.minimumWordCount &&
              ` (${question.config?.minimumWordCount} words min)`}
            {required && '*'}
            {question.help && (
              <FormFieldHelp
                name={question.name}
                label={question.label}
                help={question.help}
                showHelp={showHelp}
                setShowHelp={setShowHelp}
              />
            )}
          </label>
        )}
        {question.description && (
          <div
            className={cx(styles.description, {
              [styles.italic]: question.config?.style === 'italics',
            })}
          >
            <ReactMarkdown>
              {t(question.description, {
                keySeparator: '>',
                nsSeparator: '|',
              })}
            </ReactMarkdown>
          </div>
        )}
        {(touched || !!submitCount) && <FormFieldError error={error} />}
        <div
          className={cx({
            [styles.textBox]: !isCheckbox,
            [styles.checkboxGroup]: isCheckbox,
          })}
        >
          <label key={question.label}>
            <FastField
              key={question.name}
              name={question.name}
              component={fieldComponent(question.fieldType)}
              type={question.fieldType}
              placeholder={question.placeholder}
              value={value}
              question={question}
              context={context}
              disabled={disabled}
              validate={(value) => validateWordCount(value, question)}
              {...checkBoxProps}
            />
            {question.config?.minimumWordCount && (
              <span className={styles.wordCount}>{`Word count: ${wordCount(
                value
              )}`}</span>
            )}
            {isCheckbox && <span> {question.label} </span>}
          </label>
        </div>
        {question.config?.footer && (
          <div className={styles.footer}>
            <ReactMarkdown
              components={{
                a: ({ node, href, children, ...props }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {t(question.config.footer, {
                keySeparator: '>',
                nsSeparator: '|',
              })}
            </ReactMarkdown>
          </div>
        )}
      </div>
    )) ||
    null
  );
}

FormField.propTypes = {
  question: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  error: PropTypes.string,
  focus: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  submitCount: PropTypes.number,
};
